
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// Define the gtag function to fix TypeScript errors
declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

/**
 * A hook that tracks page views with Google Analytics 4
 * Must be used in a component wrapped with React Router's Router
 */
export const useAnalytics = () => {
  const location = useLocation();
  
  useEffect(() => {
    // Attempt to initialize Google Analytics if not already done
    if (typeof window.gtag !== 'function') {
      console.warn('⚠️ Google Analytics (gtag) not initialized. Attempting to initialize...');
      try {
        window.dataLayer = window.dataLayer || [];
        window.gtag = function(...args: any[]) {
          window.dataLayer.push(arguments);
        };
        window.gtag('js', new Date());
        window.gtag('config', 'G-YD7PXLJ142'); 
        console.log('✅ Analytics: Successfully initialized Google Analytics');
      } catch (error) {
        console.error('❌ Failed to initialize Google Analytics:', error);
      }
    }
    
    // Track page view
    if (typeof window.gtag === 'function') {
      try {
        window.gtag('event', 'page_view', {
          page_path: location.pathname,
          page_title: document.title,
          page_location: window.location.href
        });
        
        console.log('✅ Analytics: Successfully tracked page view', {
          path: location.pathname,
          title: document.title,
          url: window.location.href,
          timestamp: new Date().toISOString()
        });
      } catch (error) {
        console.error('❌ Analytics Tracking Error:', error);
      }
    } else {
      console.error('❌ Google Analytics (gtag) not available after initialization attempt.');
    }
  }, [location]);
  
  /**
   * Track a form submission event
   * @param formName - Name of the form being submitted
   * @param formData - Optional data about the form submission
   */
  const trackFormSubmission = (formName: string, formData?: Record<string, any>) => {
    if (typeof window.gtag === 'function') {
      try {
        // Standard form submission event
        const eventParams = {
          form_name: formName,
          ...formData,
          timestamp: new Date().toISOString()
        };
        
        window.gtag('event', 'form_submission', eventParams);
        
        // Track as a conversion
        window.gtag('event', 'generate_lead', {
          currency: 'USD',
          value: 1,
          ...formData
        });
        
        console.log('✅ Analytics: Successfully tracked form submission and conversion', {
          form_name: formName,
          ...formData,
          timestamp: new Date().toISOString()
        });
      } catch (error) {
        console.error('❌ Form Submission Tracking Error:', error);
      }
    } else {
      console.error('❌ Google Analytics (gtag) not available for form tracking.');
    }
  };
  
  return { trackFormSubmission };
};
